define((require) => {
    "use strict";

    const _             = require("underscore");
    const Class         = require("shared/util/Class");
    const Preconditions = require("shared/util/Preconditions");
    const Promise       = require("shared/util/Promise");
    const Syncer        = require("shared/services/sync/Syncer");
    const {Logger}        = require("shared/util/Logger");

    let sequence = 1;

    // A noop function simply used to block based on globally unique id...
    const NOOP = () => {};

    /**
     * @class {Queue}
     * A <i>queue</i> will queue all <i>actions</i> on given entities so only one action can execute at a time for
     * the queue. Here, an action is just some function to be invoked, not requiring a promise as the return value. <p>
     *
     * Different queue implementations may queue per entity or queue across entities.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     */
    return Class.extend({

        /**
         * @type {String}
         * The unique id of this queue, mainly used for logging purposes.
         */
        id: null,
        /**
         * @protected @type {SyncManager}
         * The sync manager using this queue; never null after construction.
         */
        manager: null,
        /**
         * @private @type {Deferred}
         * The private deferred that eventually will be resolved when this queue is empty, or rejected on queue error,
         * e.g. on timeout or sync failure (just one). <tt>this.promise()</tt> returns a promise obtained from this
         * deferred. <p>
         *
         * This deferred will store a reference to this queue in a (new) property called <tt>queue</tt>.
         *
         * @see #promise
         */
        _deferred: null,
        /**
         * @protected @type {{sync:Function, cancel:Function, promise:Promise, ..}[]}
         * The queued requests for this queue, in FIFO order.
         */
        _queue: null,
        /**
         * @protected @type {{sync:Function, cancel:Function, promise:Promise, ..}}
         * The currently active entry for this queue, no longer present in <tt>_queue</tt>. <p>
         *
         * Will be set on each call to <tt>_dequeue</tt>, and set to null when the entry completes. Note that even
         * though only a single entry can be set as the active entry, other entries may be doing background work.
         */
        _active: null,
        /**
         * @private @type {Boolean}
         * False if <tt>sync()</tt> has never been invoked for this queue. However, actual work may still be
         * pending, for example a parallel queue using individual target queues already started.
         */
        _synced: false,
        /**
         * @private @type {Boolean}
         * True while actively dequeuing, false otherwise (e.g. blocked or not started).
         */
        _dequeuing: false,
        /**
         * @private @type {Number}
         * The start time in ms since the epoch for this queue, used to log queue lifespan on completion.
         */
        _start: null,
        /**
         * @protected @type {Number}
         * The stop time in ms since the epoch for this queue at time of completion. <p>
         *
         * If not null, this queue has stopped - successfully or not - and is henceforth stale.
         */
        _stop: null,
        /**
         * @protected @type {[]}
         * The result(s), in order, for each sync requests synced via this queue. The actual result resolved
         * when this queue completes (is empty) will be this result, or an entry from it.
         */
        _results: null,
        /**
         * @private @type {Function}
         * The default result handler for this queue. <p>
         */
        _resultHandler: null,
        /**
         * @protected @type {Logger}
         * The log used by this queue, aptly named.
         */
        _log: null,

        /**
         * @constructor
         * Constructor, which creates this queue.
         *
         * @param {SyncManager}  manager        The sync manager using this queue; cannot be falsey.
         * @param {Function}    [resultHandler] Default queue result handler to transform each result before resolved.
         */
        constructor(manager, resultHandler) {
            this.id             = this._generateId();
            this.manager        = manager;
            this._resultHandler = resultHandler;
            this._results       = [];
            this._deferred      = Promise.deferred({queue: this});
            this._log           = new Logger(this.__typeName);
            this._dequeuing     = false;
            this._queue         = [];
            this._synced        = false;
            this._start         = Date.now();
            this._stop          = null;

            this._log.verbose(`Created queue: ${this} -> ${manager}`);
        },

        /**
         * @private
         * Generates a unique id for this queue, for easy debugging.
         *
         * @return {String} A unique id for this queue; never falsey
         */
        _generateId() {
            const regex = /([A-Z])/g;

            let id = "";
            let match;

            for (;;) {
                match = regex.exec(this.__typeName);
                if (match) {
                    id += match[1];
                } else {
                    break;
                }
            }
            return id.toLowerCase() + sequence++;
        },

        /**
         * Returns a new promise for this queue that will resolve when this queue is empty or in case of just one
         * queue failure.
         *
         * @return {Promise} A new promise for this queue; never falsey.
         *
         * @see    #sync
         * @see    #cancel
         */
        promise() {
            return Promise.create(this._deferred, {queue: this});
        },

        /**
         * Returns the size of this queue, i.e. pending sync requests. <p>
         *
         * @return {Number} The queue size; never falsey.
         */
        size() {
            return this._queue.length;
        },

        /**
         * Returns the active entry for this queue, if any. <p>
         *
         * The active entry is never in the queue, it has already been dequeued in order to sync. Note that even
         * though only a single entry can be active, other queued entries can still be performing (server side)
         * work in the background!
         *
         * @return {promise:Function->Promise, cancel:Function, blocked:Function->Boolean, ..} The active entry for
         *         this queue; can be falsey.
         * @see    #head
         */
        active() {
            return this._active;
        },

        /**
         * Returns the head of this queue, without dequeuing, which is not the same sync request as current syncing,
         * if any.
         *
         * @return {promise:Function->Promise, cancel:Function, blocked:Function->Boolean, ..} The head of this queue; can be falsey.
         */
        head() {
            return this._queue[0];
        },

        /**
         * Returns the tail of this queue, without dequeuing, which is never the same sync request as current syncing,
         * if any. <p>
         *
         * A context can use the tail to ensure it for example should wait until the queue has synced everything up
         * to the tail before continuing (for sequential queues).
         *
         * @return {promise:Function->Promise, cancel:Function, blocked:Function->Boolean, ..} The tail of this queue; can be falsey.
         */
        tail() {
            return this._queue[this._queue.length - 1];
        },

        /**
         * @private
         * Dequeues the head from this queue, which is now considered the active entry.
         *
         * @return {promise:Function->Promise, cancel:Function, blocked:Function->Boolean, sync:Function, ..}
         *         The removed head from this queue, now active.
         * @see    #active
         */
        _take() {
            this._active = this._queue.shift();
            return this._active;
        },

        /**
         * Enqueues sync requests for the specified target(s).
         *
         * @param {String|*} options The sync method name, e.g. "save" or "_save", or options like
         *                           <tt>{method:String, fn:Function, ..}</tt>. The precise structure is queue
         *                           implementation specific.
         * @param {*|[]}     targets The target(s) to enqueue the sync request for; never falsey.
         * @param {[]}       args    Arguments for the actual sync method at invocation time, if any.
         * @param {...*}     varArgs Queue implementation specific additional arguments, if any.
         *
         * @return {Promise} A promise that will resolve once all sync requests just enqueued completes; never
         *                   falsey. If this is the first and only time this queue is used, the returned promise
         *                   will complete at the same time as the promise for this entire queue, otherwise not.
         *
         * @throws Error If this queue is stale, i.e. already completed.
         */
        enqueue(...args) { // see XxxQueue._enqueue implementations for required/optional args!
            Preconditions.checkState(this._stop === null, "Queue is stale");
            let options = args[0];

            if (_.isString(options)) {
                options = {method: options};
                args[0] = options;
            }
            // Use queue default result handler if not is supplied via options...
            if (!_.isFunction(options.resultHandler)) {
                options.resultHandler = this._resultHandler; // can still be undefined
            }
            args.unshift(this._create.bind(this));
            return this._enqueue(...args) || this.promise();
        },

        /**
         * Enqueues an immutable sync method that will simply return each target when synced (resolved), effectively
         * blocking each supplied target until invoked and/or until the optional blocked handler returns false.
         * This is basically syntactic sugar for calling
         * <tt>enqueue({method: "block", blockedHandler: blockedHandler, fn: no-op-fn-that-returns-targetX}, targets)</tt>.
         *
         * @param {Function|*} [blockedHandler] An optional function that should return truthy while the request should be
         *                                      blocked, and not just enqueued, falsey when ready to be synced (no longer
         *                                      actually blocked). If no supplied, the block sync request will never
         *                                      be blocked by itself, but will adhere to queue order and may thus block
         *                                      by its location in the queue alone.
         * @param {*|[]}       [targets]        The target(s) to enqueue a blocking sync request for; can be empty
         *                                      depending on queue type (i.e. for target queues). Can also be supplied
         *                                      as the first argument when no explicit blocked handler is to be used.
         *
         * @return {Promise} A promise that will resolve once all blocking sync requests just enqueued completes; never
         *                   falsey. If this is the first and only time this queue is used, the returned promise
         *                   will complete at the same time as the promise for this entire queue, otherwise not.
         *
         * @throws Error If this queue is stale, i.e. already completed.
         * @see    SyncManager#block
         */
        block(blockedHandler, ...args) {
            let targets;
            if (!_.isFunction(blockedHandler)) {
                targets = blockedHandler; // can still be undefined
                blockedHandler = null;
            } else {
                targets = args[0]; // target or targets or undefined
            }
            const params = [{
                method: "block",
                immutable: true,
                blockedHandler,
                fn: NOOP
            }];

            if (targets) {
                params.push(targets);
            }
            return this.enqueue(...params);
        },

        /**
         * @protected @abstract
         * Abstract sub-class hook to create queue entries for specific (a) target(s), which will be passed the creator
         * function for this queue as the first argument, then all arguments, in order and as var-args, passed to
         * <tt>enqueue(..)</tt>, thus shifted one index. That is, when the context is enqueuing a sync request like
         * <tt>enqueue(method, targets, someArg1, someArg2)</tt>,
         * <tt>_enqueue(this._create, method, targets, someArg1, someArg2)</tt> will be called. The sub-class
         * implementation will then call <tt>_create(targets, creator:Function)</tt> to create actual (queue
         * implementation specific) entry types, which in turn will enqueue relevant entries. <p>
         *
         * The sub-class creator function will be called with two arguments, in order: this queue and the target to
         * create an entry for; if more than one targets are supplied to this function, the creator function will be
         * called once per such target. The creator function will return a new queue entry to queue, or falsey if
         * no entry should be enqueued, for the specified target. The entry must have a format like:
         * <pre>
         *   {
         *      promise: Function->Promise, // public, returning the promise that will complete once the entry is complete.
         *      blocked: Function->Boolean, // public, optional callback to query whether the entry is currently
         *                                  //         blocked when at the head of this queue, in which the entry can
         *                                  //         never be dequeued.
         *      sync:    Function,          // private, used to start the sync for the entry.
         *      cancel:  Function,          // public, cancel pending request (if possible)
         *      delay:   Number,            // optional number in ms for delayed/debounced syncing ("pending")
         *      ..                          // sub-class specific properties, if any
         *   }
         * </pre>
         *
         * <tt>promise</tt> will resolve once the queued entry in question is resolved/rejected, and <tt>sync</tt>
         * is called by this queue once the entry in question is dequeued and synced. Do <b>not</b> invoke <tt>sync</tt>
         * explicitly, handled by this queue.
         *
         * @param {Function->{*}} create  The create function for this queue (<tt>_create</tt>) to be called by the
         *                                sub-class implementation.
         * @param {...*}          varArgs Arguments from <tt>enqueue</tt>, in order.
         *
         * @return {Promise} An optional promise that will complete once the entry/entries just enqueued completes;
         *                   can be undefined, in which case the promise for this entire queue will be used.
         *
         * @see    #enqueue
         */
        _enqueue: null,

        /**
         * @private
         * Delegate creator function supplied on enqueue to this queue (sub-class, when <tt>_enqueue(this._create, ..)</tt>
         * is invoke to created queue entries). The queue sub-class will then call this function with the target(s)
         * in question and the actual factory method used to create the entry. <p>
         *
         * The sub-class creator function and the creation flow is documented for <tt>_enqueue</tt>.
         *
         * @param {*|[]}     targets The target(s) to create queue requests for; never falsey, but can be empty.
         * @param {Function} creator The sub-class creator function
         *                           <tt>fn(queue:Queue, target:*)->{promise:Function->Promise, sync:Function, ..}</tt>
         *                           used to create the entry to enqueue.
         *
         * @return {Promise} A promise that will resolve once all entries just queued by this call resolves; will be
         *                   an already resolved if no entries were enqueued.
         *
         * @see   _enqueue
         */
        _create(targets, creator) {
            const promises = [];
            _.each(_.isArray(targets) ? targets : [targets], function(target) {
                const entry = creator(this, target);
                if (entry) {
                    this._queue.push(entry);
                    promises.push(entry.promise());
                    this._log.verbose(`Enqueued: ${entry} -> ${this}`, entry, this);
                }
            }, this);

            // Or the returned promise will never resolve...
            if (!promises.length) {
                return null;
            }
            // Any queued request that fails will trigger this queue to fail. Since we use Promise.all
            // for multiple queued requests, we still only get one error callback...
            return (promises.length === 1 ? promises[0] : Promise.all(promises)).fail((...args) => {
                this._stopSync(true, args[0]); // true = cancel all since error
            });
        },

        /**
         * @private
         * Dequeues the head from this queue if and only if not blocked and starts the sync request for the
         * dequeued entry.
         *
         * @return {Promise} A promise for the current dequeued entry, or falsey if this queue is empty.
         *
         * @see #_state
         */
        _dequeue() {
            Preconditions.checkState(!!this.active, "Queue busy");
            const entry = this.head();
            let promise;

            if (entry) {
                // Start sync (if not already), and make sure we dequeue next once the dequeued entry completes.
                // We have to use "done", since we will cancel/purge in case of errors and we always want to
                // proceed past cancelled entries as well. We assign our "done" handler after we call "sync()",
                // to ensure our internal Syncer handlers will have a chance to complete first, i.e. to trigger
                // correct sync:state events first...
                promise = this._sync(entry, true); // true = dequeueing (false = nested queuing -> synced immediately)

                if (promise) { // will be false if blocked
                    promise.done(this._next.bind(this));
                }

                return promise;
            }

            this._state(false); // no longer dequeuing, empty
            if (this._synced && !this._stop) {
                this._stop = Date.now();
                this._log.verbose(`Queue empty: ${this}`, this);
                this._deferred.resolve(this._result(this._results));
                // Clear results, we are done (and the deferred will keep the result)...
                this._results = null;
            }
        },

        /**
         * @private
         * Performs the actual sync of the supplied entry, returning is promise unless blocked in which case
         * false is returned.
         *
         * @param {promise:Function->Promise, cancel:Function, blocked:Function->Boolean, sync:Function, ..} entry The
         *                              (queued) entry (request) to sync; never falsey.
         * @param {Boolean} [dequeuing] True if syncing because <tt>entry</tt> has just been dequeued, false
         *                              otherwise (on nested queuing, where <tt>entry</tt> is synced immediately).
         * @return {Promise|Boolean} The promise from <tt>entry.sync()</tt>, or false if <tt>entry</tt> is blocked.
         */
        _sync(entry, dequeuing) {
            if (_.isFunction(entry.blocked)) { // no all types of Queues has a blocked handler
                const blocked = entry.blocked.call(this);
                if (blocked) {
                    if (dequeuing) {
                        this._state(false); // no longer dequeuing, blocked
                        this._log.verbose(`Blocked (${blocked}), cannot dequeue: ${entry} -> ${this}`, this);
                    }
                    return false;
                }
            }
            if (dequeuing) {
                this._state(true); // now dequeuing!
                this._take();
                this._log.verbose(`Dequeued: ${entry} -> ${this}`, this);
            }
            return entry.sync();
        },

        /**
         * @private
         * Callback once the active queue entry has resolved successfully, which will call <tt>_dequeue()</tt>
         * to sync the next queued entry, if any.
         *
         * @param {*} result The result of the sync request that just completed; can be falsey/undefined.
         */
        _next(result) {
            this._results.push(result);
            this._active = null;
            this._dequeue();
        },

        /**
         * @protected @type {Function}
         * Sub-type hook to return the desired results on completion of this queue, based on the supplied result array
         * storing the result of each successfully complete sync request, in order, for this queue. <p>
         *
         * The default implementation simply returns <tt>results</tt> as-is.
         *
         * @param {[]} results The sync method invocation results; never null, but can contain falsey entries.
         * @return {*} The result to be resolved by this queue when completed (empty); can be falsey.
         */
        _result(results) {
            return results;
        },

        /**
         * @private
         * Sets the state of this queue to actively dequeuing (true) or not (false, e.g. empty or blocked), if not
         * already in such a respective state. <p>
         *
         * On state change, the sync manager using this queue will be notified which may cause events about the overall
         * sync to be triggered.
         *
         * @param {Boolean} dequeuing True if actively dequeuing, false otherwise.
         *
         * @see #_dequeue
         */
        _state(dequeuing) {
            if (dequeuing !== this._dequeuing) {
                this._dequeuing = dequeuing;
                // Note: we do not use events for this, events from specific queues not needed and too expensive, but
                // the state change for individual queues may cause events to be triggered by the sync manager, which
                // contexts then can listen for...
                this.manager._state(dequeuing);
            }
            return this._dequeuing;
        },

        /**
         * Starts the sync of this queue, if not already. <p>
         *
         * The promise returned will resolve/reject once this queue is empty or erroneous.
         *
         * @return {Promise} A promise that will resolve/reject once this queue is empty or erroneous; never null.
         *
         * @see #cancel
         * @see #promise
         */
        sync() {
            if (!this._stop) {
                this._synced = true;
                if (!this._dequeuing) {
                    this._dequeue();
                }
            }
            return this.promise();
        },

        /**
         * @protected
         * Stops this queue in case of error, if not already, optionally cancelling any pending/queue sync requests.
         */
        _stopSync(cancel, error) {
            if (!this._stop) {
                this._stop = Date.now();
                // Cancel if so specified...
                if (cancel) {
                    this._cancel();
                }
                // Signal manager that we are done dequeuing (though we could already be paused)...
                this._state(false);
                // Clear results...
                this._results = null;
                // Signal listeners that this queue had an error...
                this._deferred.reject(error);
            }
        },

        /**
         * Cancels the sync of this queue, which will cause <b>all</b> pending promises for this queue be rejected.
         * This queue is dead/stale after this call! <p>
         *
         * Sync requests already completed for this queue cannot be cancelled, and will not be affected by
         * this call. Pending sync requests may or may not be able to be cancelled, but the work performed
         * externally cannot be cancelled, e.g. some backend updating a model. The promises for each sync
         * pending sync request will be rejected with an error of <tt>Syncer.CANCELLED</tt>, but the actual
         * target may still be updated in the background sometime in the future if and when the pending promise
         * completes successfully.
         *
         * @see #sync
         * @see #promise
         */
        cancel() {
            this._stopSync(true, Syncer.CANCELLED); // checks "_stop"!
        },

        /**
         * @private
         * Cancels this entire queue by cancelling all pending sync requests, including the currently active one, if any.
         */
        _cancel() {
            // Active...
            if (this._active) {
                this._log.info(`Cancelling active entry: ${this._active} -> ${this}`, this);
                this._active.cancel();
            }
            // Queued...
            if (this.size()) {
                this._log.info(`Purging queue: ${this}`, this);
                // "_take()" updates "_active", but we already cancelled the active one, if any, and will clear
                // the property lastly in this function...
                let entry = this._take();
                while (entry) {
                    this._log.info(`Cancelling queued entry: ${entry} -> ${this}`, this);
                    entry.cancel();
                    entry = this._take(); // next?
                }
            }
            this._active = null;
        },

        /**
         * Returns the string representation of this queue.
         *
         * @return {String} The string representation of this queue; never falsey.
         */
        toString() {
            const age = (this._stop || Date.now()) - this._start;
            return `${this.__typeName}[id: ${this.id}; size: ${this.size()}; age: ${age}ms]`;
        }

    });
});



// WEBPACK FOOTER //
// ./src/shared/services/sync/Queue.js